import { apiServiceNative } from '../../../utils/fetchNative';

export type UploadVideoOptions = {
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
};

const createAbortError = () => {
  const error = new Error('Upload aborted');
  error.name = 'AbortError';
  return error;
};

const getAuthHeaders = () => {
  const headers: Record<string, string> = {
    ...(process.env.NODE_ENV === 'development' ? { 'ngrok-skip-browser-warning': '1' } : {}),
  };
  const token = localStorage.getItem('userToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const sanitizeFolderName = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getUploadEnvironmentFolder = () => {
  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

  if (!apiEndpoint) return 'development';

  try {
    const { hostname } = new URL(apiEndpoint);
    const cleverAppEnvironment = hostname.match(/^orientaction-(.+)\.cleverapps\.io$/)?.[1];

    if (cleverAppEnvironment) return sanitizeFolderName(cleverAppEnvironment);
    if (hostname === 'backend.e-orientaction.com') return 'prod';
    if (hostname.includes('preprod')) return 'preprod';
    if (hostname.includes('staging')) return 'staging';
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';

    return sanitizeFolderName(hostname);
  } catch {
    return sanitizeFolderName(apiEndpoint) || 'development';
  }
};

export const uploadVideo = (file: File, options: UploadVideoOptions = {}): Promise<string> =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('path', getUploadEnvironmentFolder());

    const xhr = new XMLHttpRequest();
    const abortUpload = () => xhr.abort();
    const cleanupSignal = () => options.signal?.removeEventListener('abort', abortUpload);

    xhr.open('POST', `${process.env.REACT_APP_API_ENDPOINT}/api/upload`);
    Object.entries(getAuthHeaders()).forEach(([key, value]) => xhr.setRequestHeader(key, value));

    xhr.upload.onprogress = event => {
      if (event.lengthComputable && options.onProgress) {
        options.onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      cleanupSignal();
      try {
        const url = JSON.parse(xhr.responseText)?.[0]?.url;
        if (xhr.status >= 200 && xhr.status < 300 && url) {
          resolve(url);
        } else {
          reject(new Error('Upload failed'));
        }
      } catch {
        reject(new Error('Upload failed'));
      }
    };

    xhr.onerror = () => {
      cleanupSignal();
      reject(new Error('Upload failed'));
    };

    xhr.onabort = () => {
      cleanupSignal();
      reject(createAbortError());
    };

    if (options.signal?.aborted) {
      xhr.abort();
      return;
    }
    options.signal?.addEventListener('abort', abortUpload);
    xhr.send(formData);
  });

export const deleteVideoByUrl = async (url: string): Promise<void> => {
  if (!url) return;
  await apiServiceNative.post('/api/upload/delete-by-url', { url });
};
